import React, { useState, useEffect } from 'react';
import '../styles/Settings.css';
import { Header, Footer, Loading, Switch, Button, Input, Modal } from '../components';
import { checkOnAuthStateChanged, cloudFunctionV2, getDocument, recordEvent, setData, setTransaction, isValidURL } from '../functions';
import { useNavigate } from 'react-router-dom';
import { APIKeyModal } from '../modalbody';

function Settings() {
    const [companyId, setCompanyId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loggedIn, setLoggedIn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [adu, setAdu] = useState(false);
    const [pool, setPool] = useState(false);
    const [constructionMaterials, setConstructionMaterials] = useState(false);
    const [stories, setStories] = useState(false);
    const [apiKey, setApiKey] = useState(null);
    const [apiKeyGenerated, setApiKeyGenerated] = useState(null);
    const [apiKeyLoading, setApiKeyLoading] = useState(false);
    const [catchHookURL, setCatchHookURL] = useState("");
    const [catchHookURLError, setCatchHookURLError] = useState(false);
    const [apiKeyModal, setApiKeyModal] = useState(false);
    const [sampleDataLoading, setSampleDataLoading] = useState(false);
    const darkBG = apiKeyModal === true ? true : false;
    const navigate = useNavigate();

    useEffect(() => {

        const fetchUserData = async() => {
            document.title = "Settings | Clozers";
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);

                const colRef = "users";
                const queryData = await getDocument(colRef, user.userId);
                if ( queryData.status === 200 ) {
                    setCompanyId(queryData.data.data.companyId);
                    fetchCompanyData(queryData.data.data.companyId);
                    fetchSettings(queryData.data.data.companyId);
                }
            }
            else {
                setLoggedIn(false);
                navigate("/login");
            }
        };

        const fetchSettings = async(newCompanyId) => {
            const colRef = "settings";
            const queryData = await getDocument(colRef, newCompanyId);
            if ( queryData.status === 200 ) {
                setLoading(false);
                const data = queryData.data.data;
                if ( data.adu !== undefined ) {
                    setAdu(data.adu);
                    setPool(data.pool);
                    setConstructionMaterials(data.constructionMaterials);
                    setStories(data.stories);
                }
                else {
                    setAdu(true);
                    setPool(true);
                    setConstructionMaterials(true);
                }
            }
            else {
                setAdu(true);
                setPool(true);
                setConstructionMaterials(true);
            }
            setLoading(false);
        };

        const fetchCompanyData = async(newCompanyId) => {
            const colRef = "companies";
            const queryData = await getDocument(colRef, newCompanyId);
            if ( queryData.status === 200 ) {
                if ( queryData.data.data.apiKey ) {
                    setApiKeyGenerated(true);
                }
                else {
                    setApiKeyGenerated(false);
                }
                if ( queryData.data.data.catchHookURL ) {
                    setCatchHookURL(queryData.data.data.catchHookURL);
                }
            }
            else {
                setApiKeyGenerated(false);
            }
        };
        
        fetchUserData();

    }, [navigate]);

    const filters = [
        {
            label: "ADU exact match",
            value: adu,
            setValue: setAdu
        },
        {
            label: "Pools exact match",
            value: pool,
            setValue: setPool
        },
        {
            label: "Construction materials exact match",
            value: constructionMaterials,
            setValue: setConstructionMaterials
        },
        {
            label: "Stories (number of floors) exact match",
            value: stories,
            setValue: setStories
        }
    ];

    const onChange = (func, value, index) => {
        func(value);
        const newData = {
            adu: index === 0 ? value : adu,
            pool: index === 1 ? value : pool,
            constructionMaterials: index === 2 ? value : constructionMaterials,
            stories: index === 3 ? value : stories
        };
        saveSettings(newData);
    };

    // 7757d1f17c9a9255ecc04ccb1eb9af96109718c7c4c9b6440edbce85c67ba582
    const generateApiKey = async() => {
        setApiKeyLoading(true);
        const url = process.env.REACT_APP_GENERATE_API_KEY;
        const params = {
            companyId: companyId
        };
        const queryData = await cloudFunctionV2(url, params);
        if ( queryData.status === 200 ) {
            setApiKey(queryData.apiKey);
            setApiKeyGenerated(true);
            setApiKeyModal(true);
        }
        else {
            setApiKeyGenerated(false);
            console.log(queryData.error);
        }
        setApiKeyLoading(false);
    };

    const onChangeCatchHookURL = async(value) => {
        setCatchHookURL(value);


        if ( !isValidURL(value) && value !== "" ) {
            setCatchHookURLError(true);
            return;
        }
        setCatchHookURLError(false);
        await setTransaction("companies", companyId, "catchHookURL", value === "" ? null : value, null);
    };

    const sendSampleData = async() => {
        setSampleDataLoading(true);
        const sampleWebhookURL = process.env.REACT_APP_SAMPLE_WEBHOOK_URL;
        const params = {
            url: catchHookURL,
            params: {
                status: 200,
                propertyId: "12345",
                address: "123 Main St, Tampa, FL 12345",
                confidence: "High",
                url: `https://app.clozers.co/run-comps/test/test`,
                arv: 500000,
                arvRange: {
                    lower: 490000,
                    upper: 510000
                },
                message: `
							Clozers ARV for 111 W Osborne Ave, Tampa, FL 33603 is $500,000\n
							View the report on Clozers here - https://app.clozers.co/run-comps/12057/4167249 \n
                            Edit lead here: https://dashboard.resimpli.com/leads/details?leadsId=12345
						`
            }
        };
        await cloudFunctionV2(sampleWebhookURL, params);
        setSampleDataLoading(false);
    };

    const closeAPIKeyModal = () => {
        setApiKeyModal(false);
    };

    const saveSettings = async(newData) => {
        setSaving(true);
        const colRef = "settings";
        await setData(colRef, companyId, newData);
        setSaving(false);
        recordEvent("save_settings", {});
    };

    return (
        <div className={"settings-outer-container " + (darkBG === true ? "relative-container" : "")}>
            <Header
                loggedIn={loggedIn}
            />
            {
                darkBG === true &&
                <div className="dark-bg">
                </div>
            }
            {
                loading === true ?
                <div className="settings-inner-container">
                    <Loading />
                </div>
                :
                <div className="settings-inner-container relative-container">
                    {
                        apiKeyModal === true &&
                        <Modal
                            heading="New API Key"
                            subheading={null}
                            body={<APIKeyModal apiKey={apiKey} />}
                            type="single"
                            cancelText={null}
                            cancelButtonCTA={closeAPIKeyModal}
                            actionButtonText={"Done"}
                            actionButtonCTA={closeAPIKeyModal}
                            secondaryButtonText={null}
                            secondaryButtonCTA={null}
                            exitButtonCTA={closeAPIKeyModal}
                            loading={false}
                            actionButtonDisabled={false}
                            leftIcon={null}
                            rightIcon={null}
                            cancelLeftIcon={null}
                            cancelRightIcon={null}
                            customClass="api-key-modal-container"
                        />
                    }
                    <div className="settings-title-container">
                        <h1 className="heading-large-bold colour-primary">
                            Settings
                        </h1>
                        <span className="body-regular colour-secondary">
                            Tell us which filters you see as being essential to your comping strategy.
                        </span>
                    </div>
                    <div className="light-grey-divider">
                    </div>
                    <div className="settings-body-container">
                        <div className="settings-body-section-title-container">
                            <h3 className="body-bold colour-primary">
                                Essential filters
                            </h3>
                            <span className="body-regular colour-priamry">
                                We find the best matching comps by default, but if there are certain features you need to match exactly, just toggle the filters below.
                                <br />
                                <br />
                                <span className="text-italic">IMPORTANT: When these filters are active, we will keep expanding the recency and search 
                                    radius until at least 5 sold comps match all essential filters.</span>
                            </span>
                        </div>
                        <div className="settings-body-filters-list">
                            {
                                filters.map((filter, index) =>
                                    <div
                                        className="settings-body-filters-list-item"
                                        key={index}
                                    >
                                        <Switch
                                            label={filter.label}
                                            val={filter.value}
                                            disabled={saving}
                                            setVal={() => onChange(filter.setValue, !filter.value, index)}
                                        />
                                    </div>
                                )
                            }
                        </div>
                    </div>
                    <div className="light-grey-divider">
                    </div>
                    <div className="settings-body-container">
                        <div className="settings-body-section-title-container">
                            <h3 className="body-bold colour-primary">
                                CRM Integration
                            </h3>
                            <span className="body-regular colour-priamry">
                                Please <a className="body-regular colour-primary" rel="noreferrer" href="https://calendly.com/liam-maher/clozers-demo-form" target="_blank">book a call</a> 
                                {" "}with us for help setting up Clozers with your CRM.
                            </span>
                        </div>
                        <div className="settings-body-section-crm-integration">
                            <h3 className="body-bold colour-primary">
                                API Key
                            </h3>
                            {
                                apiKeyGenerated === true &&
                                <span className="body-regular colour-primary">
                                    Regenerating an API Key will <span className="body-bold">delete</span>{" "}
                                    your current API Key.
                                </span>
                            }
                            <div className="settings-api-key-button-container">
                                <Button
                                    buttonType="primary-button"
                                    size="large"
                                    text={apiKeyGenerated === true ? "Regenerate API Key" : "Generate API Key"}
                                    cta={() => generateApiKey()}
                                    disabled={apiKeyLoading}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={apiKeyLoading}
                                />
                            </div>
                        </div>
                        <div className="settings-body-section-crm-integration">
                            <Input
                                label={"Send ARV Results To This URL"}
                                labelType={"default"}
                                secondaryLabelGrey={"Add a Zapier link to receive ARV results"}
                                info={false}
                                value={catchHookURL}
                                onChange={(e) => onChangeCatchHookURL(e.target.value)}
                                placeholder={"https://hooks.zapier.com/hooks/catch/123456/abcdef/"}
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                                submitPromptDisabled={false}
                                submitPrompt={null}
                                onHeightChange={null}
                            />
                            {
                                catchHookURLError === true &&
                                <span className="body-regular colour-error">
                                    Please enter a valid URL
                                </span>
                            }
                            <div className="settings-send-sample-data-button-container">
                                <Button
                                    buttonType="primary-button"
                                    size="medium"
                                    text={"Send sample data"}
                                    cta={() => sendSampleData()}
                                    disabled={catchHookURL === "" || catchHookURL === null ? true : sampleDataLoading}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={sampleDataLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            }
            <Footer />
        </div>
    )
};

export default Settings;